import { useEffect, useState } from 'react';
import esriId from '@arcgis/core/identity/IdentityManager';

/**
 * Henter rå AGOL-token (ArcGIS Online OAuth-credential) fra ArcGIS
 * IdentityManager etter pålogging.
 *
 * Dette er tokenet `forvaltning-api` forventer som `Authorization: Bearer`.
 * Det er IKKE det samme som GDO-tokenet fra echotoken-tjenesten (som brukes
 * mot geodataonline-tjenester) — derfor leser vi credential direkte fra
 * portalens sharing-endepunkt via `findCredential` (som ikke trigger nytt
 * OAuth-vindu).
 *
 * @param isSignedIn  true når brukeren er logget inn via AuthService.
 * @param portalUrl   portal-base (default https://www.arcgis.com).
 */
export function useAgolToken(isSignedIn: boolean, portalUrl = 'https://www.arcgis.com'): string | undefined {
    const [token, setToken] = useState<string>();

    useEffect(() => {
        if (!isSignedIn) {
            setToken(undefined);
            return;
        }

        const sharingUrl = `${portalUrl.replace(/\/+$/, '')}/sharing/rest`;
        let cancelled = false;
        let attempts = 0;

        // Credential settes idet OAuth fullfører. Vi poller noen få ganger i
        // tilfelle den ikke er registrert i samme tick som isSignedIn ble true.
        const tryRead = () => {
            if (cancelled) return;
            const cred = esriId.findCredential(sharingUrl) as { token?: string } | undefined;
            if (cred?.token) {
                setToken(cred.token);
                return;
            }
            if (attempts++ < 10) {
                window.setTimeout(tryRead, 300);
            }
        };

        tryRead();
        return () => {
            cancelled = true;
        };
    }, [isSignedIn, portalUrl]);

    return token;
}
